import { useForm } from 'react-hook-form';
import { useTicketTaskTemplates } from '@/hooks/useTicketTemplates';
import { useTaskTypes } from '@/hooks/useTaskTypes';
import { useEventTypes } from '@/hooks/useEventTypes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

interface TaskTemplateFormProps {
  ticketTemplateId: string;
  taskTemplate?: any;
  onSuccess?: () => void;
}

export function TaskTemplateForm({ ticketTemplateId, taskTemplate, onSuccess }: TaskTemplateFormProps) {
  const { createTaskTemplate, updateTaskTemplate } = useTicketTaskTemplates(ticketTemplateId);
  const { taskTypes } = useTaskTypes();
  const { eventTypes } = useEventTypes();
  const { register, handleSubmit, watch, setValue } = useForm<any>({
    defaultValues: taskTemplate || {
      trigger_type: 'immediate',
      is_required: true,
      is_blocking: false,
    }
  });

  const triggerType = watch('trigger_type');

  const onSubmit = async (data: any) => {
    const payload = {
      ...data,
      ticket_template_id: ticketTemplateId,
      step_order: parseInt(data.step_order),
      estimated_duration_hours: data.estimated_duration_hours ? parseInt(data.estimated_duration_hours) : null,
      trigger_days_after: data.trigger_days_after ? parseInt(data.trigger_days_after) : null,
    };

    if (taskTemplate) {
      await updateTaskTemplate.mutateAsync({ id: taskTemplate.id, ...payload });
    } else {
      await createTaskTemplate.mutateAsync(payload);
    }
    onSuccess?.();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <Label>Task Type (sets name automatically)</Label>
        <Select 
          onValueChange={(value) => {
            setValue('task_type_id', value);
            const selectedType = taskTypes.find(t => t.id === value);
            if (selectedType) {
              setValue('title_template', selectedType.name);
            }
          }}
          defaultValue={taskTemplate?.task_type_id}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            {taskTypes.map((type) => (
              <SelectItem key={type.id} value={type.id}>
                {type.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <input type="hidden" {...register('title_template', { required: true })} />
      </div>

      <div>
        <Label>Description / Instructions</Label>
        <Textarea {...register('description_template')} placeholder="Detailed instructions to execute this task..." rows={4} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Order (Step)</Label>
          <Input type="number" {...register('step_order', { required: true })} placeholder="1" />
        </div>
        <div>
          <Label>Estimated Duration (hours)</Label>
          <Input type="number" {...register('estimated_duration_hours')} placeholder="2" />
        </div>
      </div>

      <div>
        <Label>When to Create This Task?</Label>
        <Select value={triggerType} onValueChange={(value) => setValue('trigger_type', value)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="immediate">Immediately on ticket creation</SelectItem>
            <SelectItem value="days_after_event">X days after an event</SelectItem>
            <SelectItem value="on_event">When an event occurs</SelectItem>
            <SelectItem value="manual">Manually</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {triggerType === 'days_after_event' && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Days After Event</Label>
            <Input type="number" {...register('trigger_days_after')} placeholder="7" />
          </div>
          <div>
            <Label>Event Type</Label>
            <Select 
              onValueChange={(value) => setValue('trigger_event_type_code', value)}
              defaultValue={taskTemplate?.trigger_event_type_code}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {eventTypes.map((type) => (
                  <SelectItem key={type.id} value={type.code}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <Label>Required Task</Label>
        <Switch 
          defaultChecked={taskTemplate?.is_required ?? true} 
          onCheckedChange={(checked) => setValue('is_required', checked)} 
        />
      </div>

      <div className="flex items-center justify-between">
        <Label>Blocks Next Tasks</Label>
        <Switch 
          defaultChecked={taskTemplate?.is_blocking ?? false}
          onCheckedChange={(checked) => setValue('is_blocking', checked)} 
        />
      </div>

      <Button type="submit" className="w-full" disabled={createTaskTemplate.isPending || updateTaskTemplate.isPending}>
        {(createTaskTemplate.isPending || updateTaskTemplate.isPending) ? 'Saving...' : taskTemplate ? 'Update Task' : 'Create Task'}
      </Button>
    </form>
  );
}
